import React from 'react';
import { Play, Pause, Square, FastForward } from 'lucide-react';
import type { SimulationState } from '../../types/simulation.types';

interface SimulationControlsProps {
  state: SimulationState | null;
  isLoading: boolean;
  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onSpeedChange: (speed: 1 | 2 | 4 | 8) => void;
}

const SPEED_OPTIONS: (1 | 2 | 4 | 8)[] = [1, 2, 4, 8];

export const SimulationControls: React.FC<SimulationControlsProps> = ({
  state,
  isLoading,
  onStart,
  onStop,
  onPause,
  onResume,
  onSpeedChange,
}) => {
  const isRunning = state?.status === 'RUNNING';
  const isPaused = state?.status === 'PAUSED';
  const hasSimulation = state !== null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between gap-4">
        {/* Playback Controls */}
        <div className="flex items-center gap-2">
          {!hasSimulation ? (
            <button
              onClick={onStart}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="w-4 h-4" />
              <span>Start Simulation</span>
            </button>
          ) : (
            <>
              {isRunning ? (
                <button
                  onClick={onPause}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Pause className="w-4 h-4" />
                  <span>Pause</span>
                </button>
              ) : isPaused ? (
                <button
                  onClick={onResume}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Play className="w-4 h-4" />
                  <span>Resume</span>
                </button>
              ) : null}

              <button
                onClick={onStop}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Square className="w-4 h-4" />
                <span>Stop</span>
              </button>
            </>
          )}
        </div>

        {/* Speed Control */}
        {hasSimulation && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-slate-600">
              <FastForward className="w-4 h-4" />
              <span className="text-sm font-medium">Speed:</span>
            </div>
            <div className="flex gap-1">
              {SPEED_OPTIONS.map((speed) => (
                <button
                  key={speed}
                  onClick={() => onSpeedChange(speed)}
                  disabled={isLoading}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    state.speed === speed
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Status Indicator */}
        {hasSimulation && (
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isRunning
                  ? 'bg-green-500 animate-pulse'
                  : isPaused
                  ? 'bg-amber-500'
                  : 'bg-slate-400'
              }`}
            />
            <span className="text-sm text-slate-600 capitalize">
              {state.status.toLowerCase()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
